import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Loader2, MessageCircle } from "lucide-react";
import { BRAND, buildWhatsAppUrl } from "./shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultationDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", goal: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please share your name and phone number.");
      return;
    }

    // Build the WhatsApp URL and open it SYNCHRONOUSLY inside the user
    // gesture so mobile browsers (Safari/Chrome iOS/Android) do NOT block it.
    const waMessage = `Hi Aditya, I'd like to book a free consultation.\n\nName: ${form.name}\nPhone: ${form.phone}${form.email ? `\nEmail: ${form.email}` : ""}${form.goal ? `\nGoal: ${form.goal}` : ""}`;
    const waUrl = buildWhatsAppUrl(waMessage);

    // Programmatic anchor click is the most reliable way to open target=_blank
    // on mobile (window.open is blocked when detached from a user gesture).
    const link = document.createElement("a");
    link.href = waUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    link.remove();

    // Fire the backend save in the background. Do not await — the WhatsApp
    // handoff must not depend on it, and the email notification to Aditya is
    // still sent server-side even if the user closes the WhatsApp tab.
    setSubmitting(true);
    axios
      .post(`${API}/consultations`, {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        goal: form.goal,
      })
      .then(() => {
        toast.success("Thanks! Aditya will reach out shortly.");
      })
      .catch((err) => {
        console.error(err);
        toast.error("We couldn't save the details. Please message Aditya on WhatsApp.");
      })
      .finally(() => {
        setSubmitting(false);
      });

    onOpenChange(false);
    setForm({ name: "", phone: "", email: "", goal: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="consultation-dialog"
        className="sm:max-w-[520px] rounded-none border border-brand-border bg-brand-surface"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl leading-tight text-brand-ink">
            Book a free consultation
          </DialogTitle>
          <DialogDescription className="text-brand-ink2 font-sans">
            Share a few details. Aditya will personally reach out within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="c-name" className="text-xs uppercase tracking-widest text-brand-ink2">Your name</Label>
            <Input
              id="c-name"
              data-testid="consultation-name-input"
              value={form.name}
              onChange={update("name")}
              placeholder="e.g. Rohan Sharma"
              className="rounded-none border-brand-border focus-visible:ring-brand-accent"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-phone" className="text-xs uppercase tracking-widest text-brand-ink2">Phone / WhatsApp</Label>
            <Input
              id="c-phone"
              data-testid="consultation-phone-input"
              value={form.phone}
              onChange={update("phone")}
              placeholder="+91 98765 43210"
              className="rounded-none border-brand-border focus-visible:ring-brand-accent"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-email" className="text-xs uppercase tracking-widest text-brand-ink2">Email (optional)</Label>
            <Input
              id="c-email"
              type="email"
              data-testid="consultation-email-input"
              value={form.email}
              onChange={update("email")}
              placeholder="you@example.com"
              className="rounded-none border-brand-border focus-visible:ring-brand-accent"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-goal" className="text-xs uppercase tracking-widest text-brand-ink2">Your goal (optional)</Label>
            <Textarea
              id="c-goal"
              data-testid="consultation-goal-input"
              value={form.goal}
              onChange={update("goal")}
              placeholder="e.g. Build strength, lose fat, train at home 4x a week"
              rows={3}
              className="rounded-none border-brand-border focus-visible:ring-brand-accent"
            />
          </div>

          <DialogFooter className="pt-2 gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="btn-outline"
              data-testid="consultation-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="btn-accent disabled:opacity-60"
              data-testid="consultation-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />}
              {submitting ? "Sending…" : "Send & open WhatsApp"}
            </button>
          </DialogFooter>
          <p className="text-xs text-brand-ink2/70 font-sans pt-1">
            No spam. Your details are used only to schedule your consultation.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
